import type { ReactNode } from 'react'
import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useEvent, useRandomId } from '@fs/zion-frontend-friends'
import _debounce from 'lodash.debounce'

import { type SuggestionGetter } from './Autosuggest'
import { type AutosuggestConfig, privateProps } from './AutosuggestState'
import { isImmediateSuggestions, isSuggestionPromise } from './util'

export type DebounceConfig = {
  /**
   * The time to wait before making a request after the user stops typing.
   * If set to 0, no debouncing will occur.
   * @default 300
   */
  wait: number

  /**
   * The maximum time to wait for the next request while the user is typing non-stop.
   * If set to 0, no throttling will occur.
   * @default 1000
   */
  maxWait: number

  /**
   * Whether to trigger the request on the leading edge of the timeout.
   * @default true
   */
  leading: boolean
}

export type DebounceValue =
  /**
   * The amount of time, in milliseconds, that you want to wait between calls to `getSuggestions()`
   * @default 300
   */
  | number
  /** Providing `false` can be used to turn off debouncing */
  | false
  /** Or a config object can be provided for finer tuning */
  | Partial<DebounceConfig>

export type AutosuggestProviderProps = {
  children: ReactNode
  config: AutosuggestConfig
  getSuggestions: SuggestionGetter
  highlightMatchingText?: boolean
  showListWhenFocused?: boolean
  id?: string
  debounce?: DebounceValue
}

type AutosuggestContextValue = {
  config: AutosuggestConfig
  getSuggestions: SuggestionGetter
  highlightMatchingText: boolean
  id: string
}

const AutosuggestContext = createContext<AutosuggestContextValue | null>(null)

const defaultDebounceConfig = {
  wait: 300,
  maxWait: 1000,
  leading: true,
}

function getDebounceConfig(debounce?: DebounceValue): DebounceConfig {
  if (typeof debounce === 'number') {
    return { ...defaultDebounceConfig, wait: debounce }
  }
  if (typeof debounce === 'boolean') {
    return debounce ? defaultDebounceConfig : { ...defaultDebounceConfig, wait: 0, maxWait: 0 }
  }
  if (!debounce) return defaultDebounceConfig
  return { ...defaultDebounceConfig, ...debounce }
}

// https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-list/
export function AutosuggestProvider({
  children,
  getSuggestions: unstableGetSuggestions,
  highlightMatchingText = false,
  showListWhenFocused = false,
  config,
  id: incomingId,
  debounce,
}: AutosuggestProviderProps): JSX.Element {
  if (!config) {
    throw new Error('AutosuggestProvider requires a config object from useAutosuggest() in order to function')
  }

  const id = useRandomId(incomingId)
  const { [privateProps]: api, userInput } = config
  const { inputIsFocused, setShowListWhenFocused } = api
  useEffect(() => setShowListWhenFocused(showListWhenFocused), [showListWhenFocused, setShowListWhenFocused])

  const getSuggestions = useEvent(unstableGetSuggestions)
  const { updateSuggestions, setIsLoading, suggestionsAreStale } = api

  // This is used to prevent state updates after the component has unmounted
  const mountedRef = useRef(true)
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  // only update suggestions of the latest set of requests
  const inflightRef = useRef<symbol>()

  const doFetch = useEvent((input) => {
    const gotSuggestions = getSuggestions(input)
    if (isImmediateSuggestions(gotSuggestions)) {
      mountedRef.current && updateSuggestions(gotSuggestions || [])
    } else {
      const flight = Symbol(input)
      inflightRef.current = flight

      if (isSuggestionPromise(gotSuggestions)) {
        setIsLoading(true)
        gotSuggestions.then((s) => {
          if (inflightRef.current === flight && mountedRef.current) {
            updateSuggestions(s || [], false)
          }
        })
      } else {
        // This is an array of promises
        setIsLoading(true)
        gotSuggestions.forEach((promise) => {
          promise.then((result) => {
            if (inflightRef.current === flight && mountedRef.current) {
              updateSuggestions(result || [], true)
            }
          })
        })
        Promise.allSettled(gotSuggestions).then(() => {
          if (inflightRef.current === flight && mountedRef.current) {
            setIsLoading(false)
          }
        })
      }
    }
  })

  const { wait, leading, maxWait } = getDebounceConfig(debounce)
  const debouncedFetch = useMemo(
    () =>
      _debounce(doFetch, wait, {
        maxWait,
        leading,
        trailing: true, // always true to ensure we get the latest suggestions after the user stops typing
      }),
    [doFetch, wait, leading, maxWait]
  )

  useEffect(() => {
    if (inputIsFocused && suggestionsAreStale) {
      debouncedFetch(userInput)
    }
  }, [inputIsFocused, suggestionsAreStale, userInput, debouncedFetch])

  const contextValue = useMemo(
    () => ({ id, config, getSuggestions, highlightMatchingText }),
    [config, id, highlightMatchingText, getSuggestions]
  )
  return <AutosuggestContext.Provider value={contextValue}>{children}</AutosuggestContext.Provider>
}

export const useAutosuggestContext = (): AutosuggestContextValue => {
  const context = useContext(AutosuggestContext)
  if (!context) throw new Error('this component must be wrapped in an AutosuggestProvider')
  return context
}
